import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Don't Worry; It's a Compiler Bug",
  "description": "",
  "published": "2015-12-29T00:00:00.000Z",
  "redirect_from": ["/blog/dont-worry-its-a-compiler-bug/swizec/6764"],
  "categories": "ECMAScript, Front End, JavaScript, Source-to-source compiler, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`When I was a kid writing DOS games in Pascal, I would often get stuck on a problem. After I had exhausted all my knowledge and all the help files, I would conclude, without a doubt, that something was wrong with my machine.`}</p>
    <p>{`There wasn't.`}</p>
    <p>{`When I was a teenager making subpar websites in PHP, I would often get stuck on a problem. After I had exhausted all my knowledge and all the help files and all the internet I could find, I would conclude, again without a doubt, that PHP was being shitty and running my code wrong.`}</p>
    <p>{`It wasn't.`}</p>
    <blockquote>
      <p parentName="blockquote">{`"It's never the compiler's fault."`}</p>
      <p parentName="blockquote">{`~ Smart People`}</p>
    </blockquote>
    <p>{`This is hyperbole, of course. System bugs `}<em parentName="p">{`do`}</em>{` happen. But not often.`}</p>
    <p>{`A programmer can go his or her entire career without hitting a compiler, operating system, or hardware bug. In fact, studies in the late 1970's showed that 95% of bugs are caused by programmers. Of the rest, 2% fall on system software, 2% on other software, and 1% on hardware. `}<a parentName="p" {...{
        "href": "http://blog.codinghorror.com/the-first-rule-of-programming-its-always-your-fault/"
      }}>{`[`}{`1`}{`]`}</a></p>
    <p>{`Usually, it's your fault.`}</p>
    <p>{`These days though, compilers are built by people who aren't exactly Ritchie, Stroustrup, Knuth, Wirth and the like. Don't get me wrong, compiler developers are still great guys. It just feels like modern core-tech code is missing the discipline of days gone by.`}</p>
    <p>{`After being a mess for a `}<strong parentName="p">{`long`}</strong>{` time, PHP only recently started getting good. Ruby didn't even have a well-defined grammar until 2013 `}<a parentName="p" {...{
        "href": "http://programmingisterrible.com/post/42432568185/how-to-parse-ruby"
      }}>{`[`}{`2`}{`]`}</a>{`. There are so many different JavaScript compilers and interpreters (called browsers) that you're supposed to test your code in all of them.`}</p>
    <p>{`It's a mess.`}</p>
    <p>{`It's an even bigger mess now that everyone is `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Source-to-source_compiler",
        "title": "Source-to-source compiler"
      }}>{`transpiling`}</a>{` code from ES5 to ES6. In theory, this shouldn't be a big deal. You take code from one version of the language, and you turn it into another version. Just a bit of back-porting and some syntax sugar converting.`}</p>
    <p>{`In practice, it means there's another compiler in your stack; two, if you're using a dependency management system like Webpack as well.`}</p>
    <h2 {...{
      "id": "so-i-got-hit-by-a-compiler-bug-"
    }}>{`So I got hit by a compiler bug ...`}</h2>
    <p>{`Three weeks ago, I realized `}<em parentName="p">{`why`}</em>{` Our App(tm) didn't work on Safari. Some of my code was using ES6's `}<inlineCode parentName="p">{`Map`}</inlineCode>{`, which Chrome supports, but Safari does not. I thought `}<a parentName="p" {...{
        "href": "https://babeljs.io/"
      }}>{`Babel`}</a>{` would deal with that out of the box, but I was wrong.`}</p>
    <p>{`Something to do with `}<inlineCode parentName="p">{`Map`}</inlineCode>{` being a semantic feature and Babel needing to use polyfills that implement `}<inlineCode parentName="p">{`Map`}</inlineCode>{` in pure ES5. This should be a simple configuration tweak.`}</p>
    <p>{`Well, we were still on Babel 5 and Babel 6 had come out a month prior. In this brave new world of open-source JavaScript, where shiny toys are king and long-term-support is even rarer than developers making a living from donations, that meant all documentation for Babel 5 was gone. Wiped clean. Nowhere to be found.`}</p>
    <p>{`What do you mean you're still using technology that's outdated by a whole entire month!? Get with the times, daddy-o!`}</p>
    <p>{`You see, in theory, you'd want to avoid updating your build system for as long as possible. A lot of unknown unknowns can rear their ugly heads and bite you in the ass. But I had a bug to fix and a system in production.`}</p>
    <p>{`Ho boy.`}</p>
    <p>{`The upgrade path is simple: remove old packages, install new packages, tweak some configs. In the new system, you need 6 packages to do syntax transpiling `}<em parentName="p">{`and`}</em>{` semantic polyfills; like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-son"
      }}>{`// dependencies in package.json
    "babel-core": "^6.3.15",
    "babel-loader": "^6.2.0",
    "babel-plugin-transform-runtime": "^6.3.13",
    "babel-polyfill": "^6.3.14",
    "babel-preset-es2015": "^6.3.13",
    "babel-runtime": "^6.3.13",
`}</code></pre>
    <p>{`That's a lot of packages to install for one piece of Stuff, but hey, I'm sure that won't be painful to update in two months at all …`}</p>
    <p>{`You also need to add a `}<inlineCode parentName="p">{`babel-polyfill`}</inlineCode>{` entry to your Webpack config and enable the `}<inlineCode parentName="p">{`transform-runtime`}</inlineCode>{` and `}<inlineCode parentName="p">{`es2015`}</inlineCode>{` plugins. That's how Webpack knows to include the polyfills and to transpile ES6 syntax into ES5.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`entry: {
            // ...
            babel_polyfill: 'babel-polyfill'
},
loaders: [
            {
                test: /\\.js$/,
                include: [
                    path.resolve(__dirname, "app/assets/javascripts")
                ],
                exclude: /node_modules/,
                loader: 'babel-loader',
                query: {
                    plugins: ['transform-runtime'],
                    presets: ['es2015']
                }
            },
`}</code></pre>
    <p>{`Easy enough. The code transpiled without error, the app loaded fine, and it even worked in Safari. Yass!`}</p>
    <p><em parentName="p">{`"Yo, when you check my PR, make sure you click around a bit,"`}</em>{` I said to my coworker as I submitted a pull request. `}<em parentName="p">{`"It should be fine really. The only way something broke is if there was a bug in Babel 5 that we were unknowingly relying on to make our code work. I don't foresee any issues, really. Compilers usually don't have bugs."`}</em></p>
    <p>{`He didn't click around. It wasn't fine.`}</p>
    <p>{`An hour after he closed my pull request, the intern started complaining on Slack:`}</p>
    <p><em parentName="p">{`"Hey, my code doesn't work anymore. I don't know what happened. I try to test this thing and it just doesn't load."`}</em></p>
    <p>{`That's … odd. That really shouldn't be broken.`}</p>
    <p><em parentName="p">{`"Did you update from latest master?"`}</em></p>
    <p>{`He did. The error was short and cryptic:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`TypeError: (0 , _typeof3.default) is not a function
`}</code></pre>
    <p>{`After jumping through the stack trace, we found the error deep inside the bowels of some sort of polyfill for `}<inlineCode parentName="p">{`typeof`}</inlineCode>{`. You know something weird is going on when `}<inlineCode parentName="p">{`typeof`}</inlineCode>{` throws a type error.`}</p>
    <p>{`In this case, the error was caused by `}<inlineCode parentName="p">{`typeof`}</inlineCode>{` circularly trying to use itself in every instance our code used it in. Awesome.`}</p>
    <h2 {...{
      "id": "great-now-what"
    }}>{`Great. Now what?`}</h2>
    <p>{`So what do you do when you have to deploy tonight, the compiler is messing up your code, and `}<a parentName="p" {...{
        "href": "https://phabricator.babeljs.io/T6644"
      }}>{`a ticket for the bug`}</a>{` has been open for three weeks?`}</p>
    <p>{`Rolling back your change brings back the old bug. Fixing it yourself would take too long because it's a hairy problem, and getting it released upstream is a pain-in-the-ass.`}</p>
    <p>{`Luckily, this is JavaScript. Not everything has to go through your build stack. There's this magical thing called "an external dependency".`}</p>
    <p>{`We were already using `}<a parentName="p" {...{
        "href": "https://lodash.com/"
      }}>{`Lodash`}</a>{` as an external dependency. That means we were adding a `}<inlineCode parentName="p">{`<script></script>`}</inlineCode>{` tag to load Lodash from public content delivery networks (CDN). This improves load times because users might already have it cached, and it improves our build times because Webpack and Babel have less code to process.`}</p>
    <p>{`In this case, it also saved the day. Lodash has a ton of type helpers.`}</p>
    <p>{`All it took to make the codebase ready for deploy was to change all instances of `}<inlineCode parentName="p">{`typeof`}</inlineCode>{` into Lodash's type helpers — things like `}<inlineCode parentName="p">{`_.isObject`}</inlineCode>{` and `}<inlineCode parentName="p">{`_.isFunction`}</inlineCode>{`.`}</p>
    <p>{`As luck would have it, we'd only used `}<inlineCode parentName="p">{`typeof`}</inlineCode>{` in five places. My change only caused one bug that got through code review and into production!`}</p>
    <p>{`Great success! `}{`\\`}{`o/`}</p>
    <p>{`Babel's `}<inlineCode parentName="p">{`typeof`}</inlineCode>{` bug has since been fixed, by the way. Although the last comment on its discussion thread says `}<em parentName="p">{`"Uhh ... I still get this when using babel-plugin-transform-runtime"`}</em>{`. Guess we're waiting for the version dependency to propagate through the world.`}</p>
    <h2 {...{
      "id": "so-what-went-wrong-actually"
    }}>{`So what went wrong, actually?`}</h2>
    <p>{`Uh, I got hit by a compiler bug in Babel 6. Weren't you reading?`}</p>
    <p>{`That's more of a symptom than a root cause, though. Here's the real sequence of events that lead to the only time I found a compiler bug in 19 years of programming.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Failure to test code in multiple browsers`}</p>
        <ul parentName="li">
          <li parentName="ul">{`I thought we had polyfills; we didn't.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Lack of automated tests`}</p>
        <ul parentName="li">
          <li parentName="ul">{`I relied on coworkers to "click around" to find problems.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Merging bad things to master branch and not having a good rollback strategy`}</p>
        <ul parentName="li">
          <li parentName="ul">{`If you break the master, you block the production deploy`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Haphazardly updating core parts of the build stack`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Babel throwing old versions out the window on upgrades`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Using bleeding-edge technologies`}</p>
      </li>
    </ol>
    <p>{`All in all, this was a very avoidable problem. But now I know, when you're on the bleeding edge, it totally `}<em parentName="p">{`can`}</em>{` be a compiler bug.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      